import React, { useEffect, useState } from 'react';
import { fetchWidgetData, fetchHoldingsData } from './api';
import DynamicWidget from './components/DynamicWidget';
import TickerCarousel from './components/TickerCarousel';
import YahooWidgetsCarousel from './components/YahooWidgetsCarousel';
import HoldingsWidget from './components/HoldingsWidget';
import PortfolioHealthWidget from './components/PortfolioHealthWidget';
import AIInsightsWidget from './components/AIInsightsWidget';
import LifeEventsWidget from './components/LifeEventsWidget';
import AdvisorCornerWidget from './components/AdvisorCornerWidget';
import PortfolioStoryWidget from './components/PortfolioStoryWidget';
import WealthChatBot from '../wealthChatbot/WealthChatbot';

const UserTemplateDashboard = () => {
    const [userId, setUserId] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [widgetData, setWidgetData] = useState({});
    const [isLoading, setIsLoading] = useState({});
    const [errors, setErrors] = useState({});
    const [holdings, setHoldings] = useState([]);
    const useCaseId = 'wealth_management';

    const calculateProjections = (currentAUM) => {
        const currentYear = new Date().getFullYear();
        const annualGrowthRate = 0.07;

        const yearsTo2030 = 2030 - currentYear;
        const yearsTo2040 = 2040 - currentYear;

        const projection2030 = Math.round(currentAUM * Math.pow(1 + annualGrowthRate, yearsTo2030));
        const projection2040 = Math.round(currentAUM * Math.pow(1 + annualGrowthRate, yearsTo2040));

        return { "2030": projection2030, "2040": projection2040 };
    };

    const darkBgStyle = {
        '--bg0': '#111827',
        '--bg1': '#1f2937',
        background: `
      radial-gradient(900px 500px at 85% 25%, rgba(16, 185, 129, .12) 0%, rgba(16, 185, 129, 0) 55%), 
      radial-gradient(900px 600px at 15% 80%, rgba(20, 184, 166, .08) 0%, rgba(20, 184, 166, 0) 55%), 
      linear-gradient(180deg, var(--bg0), var(--bg1))
    `
    };

    useEffect(() => {
        const storedUserId = sessionStorage.getItem('user_id') || 'default_user';
        const storedCustomerId = sessionStorage.getItem('customer_id') || 'CUST007';
        setUserId(storedUserId);
        setCustomerId(storedCustomerId);

        const fetchAllData = async () => {
            const allWidgetTypes = ['portfolio', 'performance', 'projections', 'sectorAllocation', 'marketSentiment', 'assetPerformance'];
            const initialLoadingState = {};
            allWidgetTypes.forEach(type => {
                initialLoadingState[type] = true;
            });
            setIsLoading(initialLoadingState);


            try {
                const portfolioData = await fetchWidgetData('portfolio', 'DATABASE', storedUserId, useCaseId);
                setWidgetData(prev => ({ ...prev, portfolio: portfolioData }));
                setIsLoading(prev => ({ ...prev, portfolio: false }));

                if (portfolioData?.totalAum) {
                    const calculatedProjections = calculateProjections(portfolioData.totalAum);
                    setWidgetData(prev => ({ ...prev, projections: calculatedProjections }));
                    setIsLoading(prev => ({ ...prev, projections: false }));
                }
            } catch (error) {
                setErrors(prev => ({ ...prev, portfolio: error.message }));
                setIsLoading(prev => ({ ...prev, portfolio: false }));
            }

            const sqlAndNovaWidgets = [
                { type: 'performance', source: 'SQL' },
                { type: 'sectorAllocation', source: 'SQL' },
                { type: 'marketSentiment', source: 'NOVA' },
                { type: 'assetPerformance', source: 'SQL' }
            ];

            const widgetPromises = sqlAndNovaWidgets.map(widget =>
                fetchWidgetData(widget.type, widget.source, storedUserId, useCaseId)
                    .then(data => {
                        setWidgetData(prev => ({ ...prev, [widget.type]: data }));
                        setIsLoading(prev => ({ ...prev, [widget.type]: false }));
                        return { success: true, type: widget.type };
                    })
                    .catch(error => {
                        setErrors(prev => ({ ...prev, [widget.type]: error.message }));
                        setIsLoading(prev => ({ ...prev, [widget.type]: false }));
                        return { success: false, type: widget.type, error };
                    })
            );

            const holdingsPromise = fetchHoldingsData(storedUserId)
                .then(holdingsData => {
                    setHoldings(holdingsData);
                })
                .catch(error => {
                    // Error handled silently
                });

            // Wait for all parallel requests to complete
            await Promise.all([...widgetPromises, holdingsPromise]);
        };

        fetchAllData();
    }, []);

    return (
        <div className="min-h-screen pb-8" id="dashboard-main" style={darkBgStyle}>
            <TickerCarousel />

            <div className="w-full px-5 sm:px-8 relative">
                <div className="py-4" />
                {/* Hero Section - Main Content Card */}
                <section className="w-full rounded-[18px] border border-white/[0.06] overflow-hidden relative mb-[18px]" style={{
                    background: 'radial-gradient(700px 340px at 92% 30%, rgba(16,185,129,0.15), rgba(16,185,129,0) 62%), linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.55)'
                }}>
                    {/* Decorative overlay */}
                    <div className="absolute inset-[-2px] opacity-45 pointer-events-none" style={{
                        backgroundImage: 'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.14), rgba(255,255,255,0)), radial-gradient(1px 1px at 70% 40%, rgba(255,255,255,0.10), rgba(255,255,255,0)), radial-gradient(1px 1px at 82% 20%, rgba(255,255,255,0.08), rgba(255,255,255,0)), radial-gradient(1px 1px at 90% 55%, rgba(255,255,255,0.12), rgba(255,255,255,0))'
                    }}></div>

                    {/* Top Row - Portfolio Summary, Performance, Projections */}
                    <div className="relative grid grid-cols-1 md:grid-cols-[1.05fr_1.2fr_0.95fr] gap-[18px] p-[22px] pb-0 min-h-[250px]">
                        <DynamicWidget
                            type="portfolio"
                            dataSource="DATABASE"
                            data={widgetData.portfolio}
                            isLoading={isLoading.portfolio}
                            error={errors.portfolio}
                            title="Portfolio Summary"
                        />

                        <DynamicWidget
                            type="performance"
                            dataSource="SQL"
                            data={widgetData.performance}
                            isLoading={isLoading.performance}
                            error={errors.performance}
                            title="Performance"
                        />

                        <DynamicWidget
                            type="projections"
                            dataSource="NOVA"
                            data={widgetData.projections}
                            isLoading={isLoading.projections}
                            error={errors.projections}
                            title="Projections"
                        />
                    </div>

                    {/* Visualizations Section */}
                    <div className="relative grid grid-cols-1 md:grid-cols-[1.05fr_1.2fr_0.95fr] gap-[18px] p-[22px] pt-[22px] " style={{
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.18))'
                    }}>
                        <DynamicWidget
                            type="sectorAllocation"
                            dataSource="DATABASE"
                            data={widgetData.sectorAllocation}
                            isLoading={isLoading.sectorAllocation}
                            error={errors.sectorAllocation}
                            title="Asset Category"
                        />

                        <DynamicWidget
                            type="marketSentiment"
                            dataSource="NOVA"
                            data={widgetData.marketSentiment}
                            isLoading={isLoading.marketSentiment}
                            error={errors.marketSentiment}
                            title="Market Sentiment"
                        />

                        <DynamicWidget
                            type="assetPerformance"
                            dataSource="SQL"
                            data={widgetData.assetPerformance}
                            isLoading={isLoading.assetPerformance}
                            error={errors.assetPerformance}
                            title="Asset Performance"
                        />
                    </div>
                </section>

                {/* Yahoo Finance Widgets Carousel */}
                <YahooWidgetsCarousel />

                {/* Personalization Section - Life Events & Advisor */}
                <div className="grid grid-cols-1 md:grid-cols-[1.2fr_0.8fr] gap-4 mb-6">
                    <LifeEventsWidget userId={userId} useCaseId={useCaseId} />
                    <AdvisorCornerWidget userId={userId} useCaseId={useCaseId} />
                </div>

                {/* Portfolio Story - Full Width */}
                <div className="mb-6">
                    <PortfolioStoryWidget userId={userId} portfolioData={widgetData.portfolio} useCaseId={useCaseId} />
                </div>

                {/* Bottom Widgets - Grid layout for proper alignment with equal heights */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-stretch">
                    <div className="h-[420px]">
                        <HoldingsWidget holdings={holdings} />
                    </div>
                    <div className="h-[420px]">
                        <PortfolioHealthWidget userId={userId} useCaseId={useCaseId} />
                    </div>
                    <div className="h-[420px]">
                        <AIInsightsWidget userId={userId} useCaseId={useCaseId} />
                    </div>
                </div>

                {/* Nova Chat Bot */}
                <WealthChatBot
                    cust_id={customerId}
                    use_case_id={useCaseId}
                    advisor_id={userId}
                    tab_id="user_dashboard"
                />
            </div>
        </div>
    );
};

export default UserTemplateDashboard;